'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { RetryRefundAction } from './retry-refund-button'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { centsToPriceInput, parsePriceInputToCents } from '@mentora/app-shared/price-input'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
} from '@mentora/ui-components'
import { Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'
import { AmountField, ReasonField } from './transaction-dialog-fields'

// A course or ticket purchase's admin actions: refund (any amount up to what
// is left, access kept or revoked) once it is paid, or cancel while it is
// still pending. The server re-decides everything; this only offers what the
// purchase's state allows.

type PurchaseKind = 'course' | 'event'

export interface TransactionActions {
  refund: (input: { kind: PurchaseKind, id: string, amountCents: number, revokeAccess: boolean, reason: string })
  => Promise<ActionResult<{ state: 'issued' | 'awaiting_rail', revoked: boolean }>>
  cancel: (input: { kind: PurchaseKind, id: string, reason: string }) => Promise<ActionResult>
  retryRefund: RetryRefundAction
}

export interface TransactionActionsCardProps {
  kind: PurchaseKind
  id: string
  /** The raw `payment_status`. */
  paymentStatus: string
  /** What is left to refund, in `currency` (the charged currency). */
  refundableCents: number
  currency: string
  /** A refund is still waiting for the provider: its amount is not "left", it is in flight. */
  awaitingRefund: boolean
  actions: TransactionActions
}

export function TransactionActionsCard(props: TransactionActionsCardProps) {
  const t = useTranslations('transactions.actions')
  const pending = props.paymentStatus === 'pending'
  const paid = props.paymentStatus === 'completed' || (props.paymentStatus === 'refunded' && props.refundableCents > 0)
  // Everything left is already on its way back: nothing to offer until the
  // provider answers (or the refund is retried, from the refund list).
  const inFlight = props.awaitingRefund && props.refundableCents === 0
  if ((!pending && !paid) || inFlight)
    return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{pending ? t('pendingDescription') : props.refundableCents === 0 ? t('freeDescription') : t('paidDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-2 sm:flex-row">
        {pending ? <CancelDialog {...props} /> : <RefundDialog {...props} />}
      </CardContent>
    </Card>
  )
}

function RefundDialog({ kind, id, paymentStatus, refundableCents, currency, actions }: TransactionActionsCardProps) {
  const t = useTranslations('transactions.actions')
  const { showError, showSuccess } = useActionToast()
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState(centsToPriceInput(refundableCents))
  const [revoke, setRevoke] = useState(false)
  const [reason, setReason] = useState('')
  const [isPending, startTransition] = useTransition()

  // Nothing was paid (a free or fully-refunded-but-live purchase): the only
  // thing to do is take the access away.
  const nothingToReturn = refundableCents === 0
  const amountCents = nothingToReturn ? 0 : parsePriceInputToCents(amount)
  const live = paymentStatus === 'completed'
  // Paying back everything always takes the access; the server enforces it too.
  const whole = amountCents !== null && amountCents === refundableCents
  const revokes = live && (whole || revoke)
  const valid = amountCents !== null && amountCents <= refundableCents && (amountCents > 0 || revokes) && reason.trim().length >= 3

  function submit() {
    if (amountCents === null)
      return
    startTransition(async () => {
      const result = await actions.refund({ kind, id, amountCents, revokeAccess: revokes, reason: reason.trim() })
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(result.data?.state === 'awaiting_rail' ? t('toast.awaitingRail') : nothingToReturn ? t('toast.revoked') : t('toast.refunded'))
      setOpen(false)
      setReason('')
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" className="w-full sm:w-auto" onClick={() => setOpen(true)}>{nothingToReturn ? t('revokeButton') : t('refundButton')}</Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{nothingToReturn ? t('revokeTitle') : t('refundTitle')}</DialogTitle>
          <DialogDescription>{nothingToReturn ? t('revokeDescription') : t('refundDescription')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {!nothingToReturn && <AmountField id={`refund-amount-${id}`} value={amount} onChange={setAmount} maxCents={refundableCents} currency={currency} />}
          {live && !nothingToReturn && (
            <div className="flex items-start gap-3">
              <Switch id={`refund-revoke-${id}`} checked={revokes} disabled={whole} onCheckedChange={setRevoke} />
              <div className="space-y-1">
                <Label htmlFor={`refund-revoke-${id}`}>{t('revokeLabel')}</Label>
                <p className="text-xs text-muted-foreground">{whole ? t('revokeForced') : t('revokeHint')}</p>
              </div>
            </div>
          )}
          <ReasonField id={`refund-reason-${id}`} value={reason} onChange={setReason} />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={isPending}>{t('close')}</Button>
          </DialogClose>
          <Button onClick={submit} disabled={isPending || !valid}>
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {nothingToReturn ? t('revokeButton') : t('refundConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CancelDialog({ kind, id, actions }: TransactionActionsCardProps) {
  const t = useTranslations('transactions.actions')
  const { showError, showSuccess } = useActionToast()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [isPending, startTransition] = useTransition()

  function submit() {
    startTransition(async () => {
      const result = await actions.cancel({ kind, id, reason: reason.trim() })
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(t('toast.cancelled'))
      setOpen(false)
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button variant="outline" className="w-full sm:w-auto" onClick={() => setOpen(true)}>{t('cancelButton')}</Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('cancelTitle')}</DialogTitle>
          <DialogDescription>{t('cancelDescription')}</DialogDescription>
        </DialogHeader>
        <ReasonField id={`cancel-reason-${id}`} value={reason} onChange={setReason} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={isPending}>{t('close')}</Button>
          </DialogClose>
          <Button onClick={submit} disabled={isPending || reason.trim().length < 3}>
            {isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
            {t('cancelConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
